/**
 * Composable générique pour le chargement de données
 * 
 * Centralise le pattern de chargement avec gestion automatique du loading et des erreurs.
 * Remplace les patterns répétitifs de try/catch/loading dans les composables Data.
 * 
 * @example
 * // Utilisation simple
 * const { loading, loadData } = useDataLoader()
 * const data = await loadData(() => service.list())
 * 
 * @example
 * // Avec plusieurs loaders
 * const { loading, loadData } = useDataLoader({ initialLoading: true })
 * const loaderCalls = useDataLoader({ showErrorToast: false })
 * 
 * @example
 * // Avec transformation
 * const { loadData } = useDataLoader({
 *   transform: (data) => data.items || []
 * })
 * 
 * Limite: 150 lignes max
 */

import { ref } from 'vue'
import { toast } from 'vue-sonner'
import { usePostHog } from './usePostHog.js'

/**
 * Crée un loader de données avec gestion automatique du loading et des erreurs
 * @param {Object} options - Options de configuration
 * @param {boolean} options.initialLoading - État initial du loading (défaut: false)
 * @param {boolean} options.showErrorToast - Afficher un toast en cas d'erreur (défaut: true)
 * @param {string} options.defaultErrorMessage - Message d'erreur par défaut
 * @param {Function} options.onError - Callback appelé en cas d'erreur
 * @param {Function} options.transform - Fonction pour transformer les données après chargement
 * @returns {Object} - { loading, error, loadData, clearError }
 */
export function useDataLoader(options = {}) {
  const {
    initialLoading = false,
    showErrorToast = true,
    defaultErrorMessage = 'Erreur lors du chargement des données',
    onError = null,
    transform = null
  } = options

  const loading = ref(initialLoading)
  const error = ref(null)
  const { captureException } = usePostHog()

  /**
   * Charge des données avec gestion automatique du loading et des erreurs
   * @param {Function} loadFn - Fonction qui retourne une Promise (appel API/service)
   * @param {Object} loadOptions - Options spécifiques à ce chargement
   * @param {string} loadOptions.errorMessage - Message d'erreur personnalisé
   * @param {boolean} loadOptions.showErrorToast - Afficher un toast (override de l'option globale)
   * @param {Function} loadOptions.transform - Transformation spécifique (override de l'option globale)
   * @param {Function} loadOptions.onSuccess - Callback appelé en cas de succès
   * @returns {Promise<*>} - Résultat de la requête
   */
  const loadData = async (loadFn, loadOptions = {}) => {
    const {
      errorMessage = defaultErrorMessage,
      showErrorToast: showToast = showErrorToast,
      transform: localTransform = transform,
      onSuccess = null
    } = loadOptions

    loading.value = true
    error.value = null

    try {
      const result = await loadFn()
      const transformedResult = localTransform ? localTransform(result) : result

      if (onSuccess) {
        onSuccess(transformedResult)
      }

      return transformedResult
    } catch (err) {
      const message = errorMessage || err.message || defaultErrorMessage
      error.value = message

      // Capture error in PostHog with context
      const errorContext = {
        error_type: 'data_loading_error',
        error_message: message,
        data_type: loadOptions.data_type || options.data_type || null,
        endpoint: loadOptions.endpoint || null
      }
      captureException(err, errorContext)

      if (showToast) {
        toast.error(message)
      }

      if (onError) {
        onError(err, message)
      }

      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Réinitialise l'état d'erreur
   */
  const clearError = () => {
    error.value = null
  }

  return {
    loading,
    error,
    loadData,
    clearError
  }
}
